import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

type AnalyticsEventParams = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const GTM_CONTAINER_ID =
  (import.meta.env.VITE_GTM_ID as string | undefined) || 'GTM-M8SC2CFH';
const GA_ENV_OVERRIDE = import.meta.env.VITE_GA4_ENV as string | undefined;
const GA_DEBUG_ENABLED =
  import.meta.env.VITE_GA4_DEBUG === 'true' || import.meta.env.VITE_GA4_DEBUG === '1';
const GA_EVENT_ALLOWLIST = (import.meta.env.VITE_GA4_EVENT_ALLOWLIST as string | undefined)
  ?.split(',')
  .map((eventName) => eventName.trim())
  .filter(Boolean);
const GA_IGNORE_PAGE_PREFIXES = (import.meta.env.VITE_GA4_IGNORE_PAGE_PREFIXES as string | undefined)
  ?.split(',')
  .map((prefix) => prefix.trim())
  .filter(Boolean);
const ANALYTICS_ENABLED = Boolean(GTM_CONTAINER_ID);
let analyticsInitialized = false;
const PAGE_VIEW_IGNORED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
];

const getIsPwaInstalled = (): boolean => {
  if (typeof window === 'undefined') return false;
  const isStandaloneMode = window.matchMedia?.('(display-mode: standalone)')?.matches ?? false;
  const isIosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return isStandaloneMode || isIosStandalone;
};

const getDeviceType = (): 'mobile' | 'desktop' => {
  if (typeof window === 'undefined') return 'desktop';
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData?.mobile) return 'mobile';
  const ua = navigator.userAgent || '';
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return 'mobile';
  if (window.innerWidth && window.innerWidth < 768) return 'mobile';
  return 'desktop';
};

const getContextParams = (): AnalyticsEventParams => ({
  app_env: GA_ENV_OVERRIDE || import.meta.env.MODE,
  app_platform: getIsPwaInstalled() ? 'pwa' : 'web',
  device_type: getDeviceType(),
  pwa_installed: getIsPwaInstalled(),
  ...(GA_DEBUG_ENABLED ? { debug_mode: true } : {}),
});

const shouldTrackEvent = (eventName: string): boolean => {
  if (!GA_EVENT_ALLOWLIST || GA_EVENT_ALLOWLIST.length === 0) {
    return true;
  }
  return GA_EVENT_ALLOWLIST.includes(eventName);
};

const normalizePageUrl = (rawUrl: string): { pagePath: string; pageLocation: string } => {
  try {
    const url = new URL(rawUrl);
    PAGE_VIEW_IGNORED_PARAMS.forEach((param) => url.searchParams.delete(param));
    return {
      pagePath: url.pathname,
      pageLocation: `${url.origin}${url.pathname}${url.search}`,
    };
  } catch (_) {
    return { pagePath: rawUrl, pageLocation: rawUrl };
  }
};

const shouldIgnorePageView = (pagePath: string): boolean => {
  if (!GA_IGNORE_PAGE_PREFIXES || GA_IGNORE_PAGE_PREFIXES.length === 0) {
    return false;
  }
  return GA_IGNORE_PAGE_PREFIXES.some((prefix) => pagePath.startsWith(prefix));
};

export const initAnalytics = (): void => {
  if (!ANALYTICS_ENABLED || analyticsInitialized || typeof window === 'undefined') return;

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  analyticsInitialized = true;
  if (GA_DEBUG_ENABLED) {
    console.info('[GTM] inicializado', {
      containerId: GTM_CONTAINER_ID,
      appEnv: GA_ENV_OVERRIDE || import.meta.env.MODE,
      debug: true,
    });
  }
};

const trackEvent = (eventName: string, params: AnalyticsEventParams = {}): void => {
  if (!ANALYTICS_ENABLED) return;
  if (!shouldTrackEvent(eventName)) return;
  initAnalytics();
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  const payload: Record<string, unknown> = {
    event: eventName,
    ...getContextParams(),
    ...params,
  };
  if (GA_DEBUG_ENABLED) {
    console.info('[GTM]', eventName, payload);
  }
  window.dataLayer.push(payload);
};

export const trackPageView = (pagePath?: string): void => {
  if (typeof window === 'undefined') return;
  const { pagePath: normalizedPath, pageLocation } = normalizePageUrl(
    pagePath ?? window.location.href
  );
  if (shouldIgnorePageView(normalizedPath)) return;
  trackEvent('page_view', {
    page_path: normalizedPath,
    page_location: pageLocation,
    page_title: document.title,
  });
};

export const trackClickCta = (
  ctaName: string,
  locationId: string,
  linkUrl?: string
): void => {
  trackEvent('click_cta', {
    link_text: ctaName,
    link_url: linkUrl,
    location_id: locationId,
  });
};

export const trackClickMenuItem = (
  itemName: string,
  itemId?: string,
  itemCategory?: string
): void => {
  trackEvent('click_menu_item', {
    item_name: itemName,
    item_id: itemId,
    item_category: itemCategory,
  });
};

export const trackSelectRole = (role: string): void => {
  trackEvent('select_role', {
    user_role: role,
  });
};

export const trackLoginSuccess = (method = 'social', role?: string): void => {
  trackEvent('login', {
    method,
    user_role: role,
  });
};

export const trackLoginError = (errorCode?: string): void => {
  trackEvent('login_error', {
    error_code: errorCode,
  });
};

export const trackOpenCart = (): void => {
  trackEvent('open_cart', {});
};

export const AnalyticsRouteTracker = (): null => {
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    if (lastPathRef.current === currentPath) return;
    lastPathRef.current = currentPath;
    trackPageView(currentPath);
  }, [location.pathname, location.search, location.hash]);

  return null;
};
